import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react"

/**
 * Coordinates keyboard navigation across the test-case grid.
 *
 * Roving tab order: the grid exposes a *single* tab stop (the `<table>` itself).
 * Individual cells are `tabIndex={-1}` so Tab no longer cycles through every
 * cell — it enters the grid once, then arrow keys move a roving focus between
 * cells. When focus re-enters the grid, it returns to the last-focused cell.
 *
 * Auto-edit handoff: when the user commits a cell with Tab / Shift+Tab /
 * Cmd+Enter, the cell requests that the *next* focused cell open straight into
 * edit mode, so rapid data entry doesn't require a click or Enter per cell.
 */
interface GridNavContextValue {
  /** Remember the cell that last held focus, so re-entry can restore it. */
  noteFocusedCell: (el: HTMLElement | null) => void
  /** Focus the remembered cell, or the first navigable cell as a fallback. */
  focusActiveCell: (container: HTMLElement) => void
  /** Ask the next focused cell to open in edit mode. */
  requestAutoEdit: () => void
  /** Read-and-clear the auto-edit request. Returns true at most once per request. */
  consumeAutoEdit: () => boolean
}

const noopContext: GridNavContextValue = {
  noteFocusedCell: () => {},
  focusActiveCell: () => {},
  requestAutoEdit: () => {},
  consumeAutoEdit: () => false,
}

const GridNavContext = createContext<GridNavContextValue>(noopContext)

/**
 * Access the grid navigation coordinator. Returns no-op defaults when rendered
 * outside a {@link GridNavProvider} so cells stay usable in isolation (tests,
 * storybook) without a surrounding grid.
 */
export function useGridNav(): GridNavContextValue {
  return useContext(GridNavContext)
}

export const GridNavProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const lastFocusedRef = useRef<HTMLElement | null>(null)
  const autoEditRef = useRef(false)

  const noteFocusedCell = useCallback((el: HTMLElement | null) => {
    lastFocusedRef.current = el
  }, [])

  const focusActiveCell = useCallback((container: HTMLElement) => {
    const remembered = lastFocusedRef.current
    if (remembered && container.contains(remembered)) {
      remembered.focus()
      return
    }
    const first = container.querySelector<HTMLElement>("[data-grid-cell]")
    first?.focus()
  }, [])

  const requestAutoEdit = useCallback(() => {
    autoEditRef.current = true
  }, [])

  const consumeAutoEdit = useCallback(() => {
    if (autoEditRef.current) {
      autoEditRef.current = false
      return true
    }
    return false
  }, [])

  const value = useMemo(
    () => ({
      noteFocusedCell,
      focusActiveCell,
      requestAutoEdit,
      consumeAutoEdit,
    }),
    [noteFocusedCell, focusActiveCell, requestAutoEdit, consumeAutoEdit],
  )

  return (
    <GridNavContext.Provider value={value}>{children}</GridNavContext.Provider>
  )
}
